/**
 * Draw something, tap the wand, watch it fall over. Then sprinkle magic on it:
 * bouncy, floaty, spinny, heavy. Drag it, throw it, tap it to make it jump.
 */

import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import {
  Anchor,
  ArrowDownToLine,
  ArrowUpDown,
  Circle,
  Feather,
  Home,
  Images,
  Pencil,
  Rocket,
  Square,
  Star,
  Timer,
  Tornado,
  Triangle,
  Trash2,
  Wand2,
} from 'lucide-preact';
import type { Behavior, Body } from '../engine/body.ts';
import type { Stroke } from '../engine/stroke.ts';
import type { Vec } from '../engine/vec.ts';
import type { Spark } from '../engine/render.ts';
import { createBody, setBehavior } from '../engine/body.ts';
import { createStroke, extendStroke, finishStroke } from '../engine/stroke.ts';
import { groupStrokes } from '../engine/group.ts';
import { World } from '../engine/world.ts';
import { drawBody, drawBodyShadow, drawSparks, drawStroke, spawnSparks, updateSparks } from '../engine/render.ts';
import { partyColors, token } from '../engine/palette.ts';
import { degreeForSize, play, playImpact } from '../engine/audio.ts';
import { TOY_LABEL, TOY_ORDER, createToy } from '../engine/toys.ts';
import type { ToyId } from '../engine/toys.ts';
import { isTap, useSurface } from '../hooks/useSurface.ts';
import { paperPattern } from '../ui/paper.ts';
import { Chip, Coach, ConfirmSheet, CrayonRow, MagicButton, ToyButton } from '../ui/controls.tsx';
import { PicturePicker } from '../ui/PicturePicker.tsx';
import type { Character } from '../engine/character.ts';
import { characterFrom, drawFace } from '../engine/character.ts';
import type { Scene } from '../lib/scene.ts';
import { sceneBounds } from '../lib/scene.ts';

const MAX_BODIES = 55;
const MAGICS: Array<{ id: Behavior; label: string; icon: JSX.Element }> = [
  { id: 'bouncy', label: 'Bouncy', icon: <ArrowUpDown size={20} strokeWidth={2.75} /> },
  { id: 'balloon', label: 'Floaty', icon: <Feather size={20} strokeWidth={2.75} /> },
  { id: 'spinner', label: 'Spinny', icon: <Tornado size={20} strokeWidth={2.75} /> },
  { id: 'heavy', label: 'Heavy', icon: <Anchor size={20} strokeWidth={2.75} /> },
];

const TOY_ICON: Record<ToyId, JSX.Element> = {
  ball: <Circle size={20} strokeWidth={2.75} />,
  block: <Square size={20} strokeWidth={2.75} />,
  star: <Star size={20} strokeWidth={2.75} />,
  triangle: <Triangle size={20} strokeWidth={2.75} />,
};

interface Grab {
  body: Body;
  /** Grab point in the body's local frame, so it swings from where you held it. */
  anchor: Vec;
  last: Vec;
  vel: Vec;
}

interface Props {
  onExit: () => void;
}

export function AliveLab({ onExit }: Props): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const worldRef = useRef(new World({ width: 1, height: 1 }));
  const strokesRef = useRef<Stroke[]>([]);
  const activeRef = useRef(new Map<number, Stroke>());
  const grabsRef = useRef(new Map<number, Grab>());
  const sparksRef = useRef<Spark[]>([]);
  const patternRef = useRef<CanvasPattern | null>(null);
  const clockRef = useRef(0);
  const facesRef = useRef(new Map<number, Character>());

  const [color, setColor] = useState(() => token('--crayon-purple'));
  const [magic, setMagic] = useState<Behavior>('bouncy');
  const [phase, setPhase] = useState<'draw' | 'play'>('draw');
  const [inkCount, setInkCount] = useState(0);
  const [aliveCount, setAliveCount] = useState(0);
  const [spaceMode, setSpaceMode] = useState(false);
  const [slowMo, setSlowMo] = useState(false);
  const [askClear, setAskClear] = useState(false);
  const [picking, setPicking] = useState(false);

  const live = useRef({ color, magic, phase });
  live.current = { color, magic, phase };

  /** The same shape keeps the same face for as long as it is alive. */
  const faceFor = (id: number, color: string): Character => {
    const existing = facesRef.current.get(id);
    if (existing) return existing;
    const made = characterFrom(id * 2246822519, color);
    facesRef.current.set(id, made);
    return made;
  };

  useEffect(() => {
    worldRef.current.gravity = spaceMode ? 90 : 1900;
    worldRef.current.wallRestitution = spaceMode ? 0.95 : 0.3;
    worldRef.current.wake();
  }, [spaceMode]);

  useEffect(() => {
    worldRef.current.timeScale = slowMo ? 0.32 : 1;
  }, [slowMo]);

  const bringToLife = useCallback(() => {
    const world = worldRef.current;
    const groups = groupStrokes(strokesRef.current);
    if (groups.length === 0) return;
    for (const group of groups) {
      const points: Vec[] = [];
      for (const s of group) points.push(...s.points);
      const body = createBody(points, group, { behavior: 'normal', angVel: (Math.random() - 0.5) * 1.5 });
      world.add(body);
      spawnSparks(sparksRef.current, body.pos, partyColors(), 12, 220);
    }
    trimBodies(world);
    strokesRef.current = [];
    activeRef.current.clear();
    setInkCount(0);
    setAliveCount(world.bodies.length);
    setPhase('play');
    play('sparkle', 0.8);
  }, []);

  /**
   * A kept picture, dropped into the playground as a real thing.
   *
   * It arrives in the drawn world coordinates of the pad it was made on, which
   * may be anywhere and any size, so it is fitted into the top of the screen
   * before being handed to the same code that brings fresh ink to life.
   */
  const bringIn = useCallback(
    (scene: Scene) => {
      const world = worldRef.current;
      const box = sceneBounds(scene);
      if (!box) return;

      const boxW = Math.max(1, box.maxX - box.minX);
      const boxH = Math.max(1, box.maxY - box.minY);
      const scale = Math.min((world.width * 0.55) / boxW, (world.height * 0.4) / boxH, 1.6);
      const cx = (box.minX + box.maxX) / 2;
      const cy = (box.minY + box.maxY) / 2;
      const toX = world.width / 2;
      const toY = world.height * 0.3;

      strokesRef.current = scene.strokes.map((s) => ({
        ...s,
        width: s.width * scale,
        points: s.points.map((p) => ({ x: toX + (p.x - cx) * scale, y: toY + (p.y - cy) * scale })),
      }));
      setInkCount(strokesRef.current.length);
      bringToLife();
    },
    [bringToLife]
  );

  const dropToy = (id: ToyId) => {
    const world = worldRef.current;
    const x = world.width * (0.25 + Math.random() * 0.5);
    const body = createToy(id, { x, y: 70 }, live.current.color);
    world.add(body);
    trimBodies(world);
    setAliveCount(world.bodies.length);
    setPhase('play');
    play('pop', 0.6);
  };

  const clearAll = () => {
    const world = worldRef.current;
    for (const b of world.bodies) spawnSparks(sparksRef.current, b.pos, partyColors(), 6, 180);
    world.clear();
    strokesRef.current = [];
    activeRef.current.clear();
    grabsRef.current.clear();
    setInkCount(0);
    setAliveCount(0);
    setPhase('draw');
    setAskClear(false);
    play('whoosh', 0.7);
  };

  useSurface(canvasRef, {
    onResize: ({ width, height }) => worldRef.current.resize(width, height),
    onFrame: (ctx, dt, { width, height }) => {
      const world = worldRef.current;
      world.update(dt);
      updateSparks(sparksRef.current, dt);

      for (const impact of world.impacts) {
        const body = world.bodies.find((b) => b.id === impact.bodyId);
        const strength = Math.min(1, impact.speed / 1600);
        // Bouncy and heavy keep their own voices; everything else rings at a
        // pitch set by how big it is, so a pile of drawn shapes plays a tune.
        const behavior = body?.behavior;
        if (behavior === 'bouncy') playImpact('boing', strength);
        else if (behavior === 'heavy') playImpact('thud', strength);
        else playImpact('pop', strength, degreeForSize(impact.radius));
      }

      if (!patternRef.current) patternRef.current = paperPattern(ctx);
      ctx.fillStyle = patternRef.current ?? token('--paper');
      ctx.fillRect(0, 0, width, height);

      clockRef.current += dt;
      for (const body of world.bodies) drawBodyShadow(ctx, body, height);
      for (const body of world.bodies) drawBody(ctx, body);

      // What a child drew becomes a somebody: their own shape, with a face on
      // it. The face is derived from the body, so the same drawing is always
      // the same character rather than a new one every frame.
      for (const body of world.bodies) {
        const face = faceFor(body.id, body.strokes[0]?.color ?? live.current.color);
        const cos = Math.cos(body.angle);
        const sin = Math.sin(body.angle);
        // Sit the face a little above the middle, in the body's own frame.
        const lift = -body.radius * 0.12;
        drawFace(ctx, face, {
          x: body.pos.x - sin * lift,
          y: body.pos.y + cos * lift,
          size: body.radius * 0.62,
          t: clockRef.current,
          angle: body.angle,
          squash: body.wobble * 2,
          mood: body.held ? 'surprised' : body.lastImpact > 0.08 ? 'surprised' : 'happy',
        });
      }
      drawSparks(ctx, sparksRef.current);
      for (const s of strokesRef.current) drawStroke(ctx, s);
      for (const s of activeRef.current.values()) drawStroke(ctx, s);
    },
    onDown: (p) => {
      const world = worldRef.current;
      const body = live.current.phase === 'play' ? world.pick(p.pos) : null;
      if (body) {
        body.held = true;
        body.asleep = false;
        const c = Math.cos(-body.angle);
        const s = Math.sin(-body.angle);
        const dx = p.pos.x - body.pos.x;
        const dy = p.pos.y - body.pos.y;
        grabsRef.current.set(p.id, {
          body,
          anchor: { x: dx * c - dy * s, y: dx * s + dy * c },
          last: { ...p.pos },
          vel: { x: 0, y: 0 },
        });
        return;
      }
      const stroke = createStroke('crayon', live.current.color, 18, Math.floor(Math.random() * 100000));
      extendStroke(stroke, { ...p.pos });
      activeRef.current.set(p.id, stroke);
    },
    onMove: (p) => {
      const grab = grabsRef.current.get(p.id);
      if (grab) {
        const c = Math.cos(grab.body.angle);
        const s = Math.sin(grab.body.angle);
        const world = { x: grab.anchor.x * c - grab.anchor.y * s, y: grab.anchor.x * s + grab.anchor.y * c };
        const next = { x: p.pos.x - world.x, y: p.pos.y - world.y };
        grab.vel = { x: (next.x - grab.body.pos.x) * 24, y: (next.y - grab.body.pos.y) * 24 };
        grab.body.pos = next;
        grab.last = { ...p.pos };
        return;
      }
      const stroke = activeRef.current.get(p.id);
      if (stroke) extendStroke(stroke, { ...p.pos });
    },
    onUp: (p) => {
      const grab = grabsRef.current.get(p.id);
      if (grab) {
        grabsRef.current.delete(p.id);
        grab.body.held = false;
        grab.body.asleep = false;
        grab.body.restTime = 0;
        if (isTap(p)) {
          // A tap sprinkles the chosen magic and gives it a hop.
          setBehavior(grab.body, live.current.magic);
          grab.body.vel = { x: (Math.random() - 0.5) * 260, y: -620 };
          grab.body.angVel = (Math.random() - 0.5) * 6;
          grab.body.wobble = 0.32;
          spawnSparks(sparksRef.current, grab.body.pos, partyColors(), 14, 300);
          play('sparkle', 0.8);
        } else {
          grab.body.vel = { x: clampSpeed(grab.vel.x), y: clampSpeed(grab.vel.y) };
          play('whoosh', Math.min(1, Math.hypot(grab.vel.x, grab.vel.y) / 1400));
        }
        return;
      }

      const stroke = activeRef.current.get(p.id);
      if (!stroke) return;
      activeRef.current.delete(p.id);
      strokesRef.current.push(finishStroke(stroke));
      setInkCount(strokesRef.current.length);
      if (live.current.phase === 'play') setPhase('draw');
    },
  });

  const nothingHere = inkCount === 0 && aliveCount === 0;

  return (
    <div class="screen">
      <div class="stage">
        <canvas ref={canvasRef} aria-label="Playground" />
        <div class="topbar">
          <ToyButton round label="Home" hideLabel onClick={onExit}>
            <Home size={24} strokeWidth={2.5} />
          </ToyButton>
          <div class="topbar__group">
            <ToyButton
              round
              label={spaceMode ? 'Turn gravity back on' : 'Turn gravity off'}
              hideLabel
              pressed={spaceMode}
              onClick={() => setSpaceMode((v) => !v)}
            >
              {spaceMode ? <ArrowDownToLine size={24} strokeWidth={2.5} /> : <Rocket size={24} strokeWidth={2.5} />}
            </ToyButton>
            <ToyButton
              round
              label="Slow motion"
              hideLabel
              pressed={slowMo}
              onClick={() => setSlowMo((v) => !v)}
            >
              <Timer size={24} strokeWidth={2.5} />
            </ToyButton>
            <ToyButton round label="Clear everything" hideLabel disabled={nothingHere} onClick={() => setAskClear(true)}>
              <Trash2 size={24} strokeWidth={2.5} />
            </ToyButton>
          </div>
        </div>

        {nothingHere && <Coach>Draw something</Coach>}
        {!nothingHere && phase === 'draw' && inkCount > 0 && <Coach>Now tap the wand</Coach>}
        {phase === 'play' && inkCount === 0 && aliveCount > 0 && <Coach>Tap a shape to make it {magicLabel(magic)}</Coach>}
      </div>

      <div class="dock">
        {phase === 'draw' ? (
          <CrayonRow value={color} onChange={setColor} />
        ) : (
          <div class="dock__row dock__row--scroll" role="group" aria-label="Magic">
            {MAGICS.map((m) => (
              <Chip
                key={m.id}
                label={m.label}
                pressed={magic === m.id}
                onClick={() => setMagic(m.id)}
              >
                {m.icon}
              </Chip>
            ))}
          </div>
        )}

        <div class="dock__row">
          <div class="dock__row dock__row--scroll" role="group" aria-label="Toy box">
            {TOY_ORDER.map((id) => (
              <Chip key={id} label={TOY_LABEL[id]} onClick={() => dropToy(id)}>
                {TOY_ICON[id]}
              </Chip>
            ))}
            <Chip label="My pictures" onClick={() => setPicking(true)}>
              <Images size={20} strokeWidth={2.75} />
            </Chip>
          </div>
          {phase === 'draw' ? (
            <MagicButton label="Make it move" accent={color} disabled={inkCount === 0} onClick={bringToLife}>
              <Wand2 size={26} strokeWidth={2.75} />
            </MagicButton>
          ) : (
            <MagicButton label="Draw more" accent={color} onClick={() => setPhase('draw')}>
              <Pencil size={26} strokeWidth={2.75} />
            </MagicButton>
          )}
        </div>
      </div>

      {askClear && (
        <ConfirmSheet title="Clear the playground?" confirmLabel="Clear it" onConfirm={clearAll} onCancel={() => setAskClear(false)} />
      )}

      {picking && (
        <PicturePicker
          title="Bring a picture in"
          onPick={(scene) => {
            setPicking(false);
            bringIn(scene);
          }}
          onCancel={() => setPicking(false)}
        />
      )}
    </div>
  );
}

function magicLabel(behavior: Behavior): string {
  return (MAGICS.find((m) => m.id === behavior)?.label ?? 'Bouncy').toLowerCase();
}

function clampSpeed(v: number): number {
  return Math.max(-2600, Math.min(2600, v));
}

/** Keep the scene playable on a phone: retire the oldest shapes past the cap. */
function trimBodies(world: World): void {
  while (world.bodies.length > MAX_BODIES) world.remove(world.bodies[0].id);
}
